import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Tabs, Tab, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, IconButton, Chip,
} from '@mui/material';
import { Close, CheckCircle } from '@mui/icons-material';
import api from '../services/api';

interface ForecastPosition {
  id: number;
  ticker: string;
  market_title: string | null;
  strategy_id: number | null;
  strategy_name: string;
  entry_price: number;
  current_price: number;
  quantity: number;
  cost: number;
  current_value: number;
  unrealized_pnl: number;
  max_return: number;
  close_time: string | null;
}

interface ManagePositionsDialogProps {
  open: boolean;
  onClose: () => void;
  positions: ForecastPosition[];
  onSuccess: () => void;
}

interface ExitResult {
  position_id: number;
  ticker: string;
  realized_pnl: number;
  status: string;
}

export default function ManagePositionsDialog({ open, onClose, positions, onSuccess }: ManagePositionsDialogProps) {
  const [tab, setTab] = useState(0);
  const [exitLoading, setExitLoading] = useState(false);
  const [exitError, setExitError] = useState<string | null>(null);
  const [exitResults, setExitResults] = useState<{ results: ExitResult[]; total_realized_pnl: number } | null>(null);
  const [stopLossValues, setStopLossValues] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  const [saveErrors, setSaveErrors] = useState<Record<number, string>>({});
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set());

  const handleClose = () => {
    if (exitLoading || savingAll) return;
    setTab(0);
    setExitError(null);
    setExitResults(null);
    setStopLossValues({});
    setSaveErrors({});
    setSavedIds(new Set());
    onClose();
  };

  const handleConfirmExit = async () => {
    setExitLoading(true);
    setExitError(null);
    try {
      const res = await api.post('/api/v1/positions/reduce', {
        positions: positions.map(p => ({ position_id: p.id })),
      });
      setExitResults(res.data);
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 2000);
    } catch (err: any) {
      setExitError(err.response?.data?.detail || 'Failed to exit positions');
    } finally {
      setExitLoading(false);
    }
  };

  const handleSaveStopLoss = async (posId: number) => {
    const val = stopLossValues[posId];
    if (val === undefined || val === '') return;
    const numVal = parseFloat(val);
    if (isNaN(numVal) || numVal < 0 || numVal > 1) {
      setSaveErrors(prev => ({ ...prev, [posId]: 'Must be 0.00–1.00' }));
      return;
    }
    setSavingId(posId);
    setSaveErrors(prev => { const n = { ...prev }; delete n[posId]; return n; });
    try {
      await api.put(`/api/v1/positions/${posId}`, { stop_loss: numVal });
      setSavedIds(prev => new Set(prev).add(posId));
    } catch (err: any) {
      setSaveErrors(prev => ({ ...prev, [posId]: err.response?.data?.detail || 'Save failed' }));
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveAll = async () => {
    setSavingAll(true);
    const toSave = Object.entries(stopLossValues).filter(([, v]) => v !== '');
    for (const [idStr] of toSave) {
      await handleSaveStopLoss(Number(idStr));
    }
    setSavingAll(false);
  };

  const totalCost = positions.reduce((s, p) => s + (p.cost ?? p.entry_price * p.quantity), 0);
  const totalUnrealized = positions.reduce((s, p) => s + (p.unrealized_pnl ?? 0), 0);
  const hasChanges = Object.values(stopLossValues).some(v => v !== '');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { backgroundColor: '#FDF6EC', border: '1px solid #F0E4CE' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Manage {positions.length} Position{positions.length !== 1 ? 's' : ''}
        </Typography>
        <IconButton size="small" onClick={handleClose} disabled={exitLoading || savingAll}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: '1px solid #F0E4CE' }}>
        <Tab label="Exit Positions" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="Set Stop-Loss" sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      <DialogContent sx={{ pt: 2 }}>
        {/* Tab 1: Exit Positions */}
        {tab === 0 && (
          <>
            {exitResults ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircle sx={{ fontSize: 48, color: '#27AE60', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Positions Exited
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: exitResults.total_realized_pnl >= 0 ? '#27AE60' : '#C0392B' }}>
                  Realized P&L: {exitResults.total_realized_pnl >= 0 ? '+' : ''}${exitResults.total_realized_pnl.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Closing automatically...
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: '#4A3728' }}>Market</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#4A3728' }}>Side</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#4A3728' }}>Cost</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#4A3728' }}>Unrealized P&L</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {positions.map(pos => {
                        const cost = pos.cost ?? pos.entry_price * pos.quantity;
                        const pnl = pos.unrealized_pnl ?? 0;
                        return (
                          <TableRow key={pos.id}>
                            <TableCell sx={{ fontSize: 13, maxWidth: 260 }}>
                              <Typography variant="body2" noWrap>{pos.market_title || pos.ticker}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={pos.entry_price < 0.5 ? 'No' : 'Yes'} size="small" sx={{ fontSize: 11, fontWeight: 600 }} />
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 13 }}>${cost.toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600, color: pnl >= 0 ? '#27AE60' : '#C0392B' }}>
                              {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ fontWeight: 700, borderTop: '2px solid #F0E4CE' }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, borderTop: '2px solid #F0E4CE' }}>${totalCost.toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, borderTop: '2px solid #F0E4CE', color: totalUnrealized >= 0 ? '#27AE60' : '#C0392B' }}>
                          {totalUnrealized >= 0 ? '+' : ''}${totalUnrealized.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: '1px solid #E8A045', bgcolor: 'rgba(232,160,69,0.08)' }}>
                  <Typography variant="body2" sx={{ color: '#7A5F4E' }}>
                    <strong>Quantato is a software platform, not a broker.</strong> The fastest way to exit is to go directly
                    to <a href="https://kalshi.com" target="_blank" rel="noopener noreferrer" style={{ color: '#3A8C8C', fontWeight: 600 }}>kalshi.com</a>.
                    Exit orders are routed through your own Kalshi API connection, but execution depends on market liquidity and Kalshi API availability.
                  </Typography>
                </Box>

                {exitError && <Alert severity="error" sx={{ mt: 2 }}>{exitError}</Alert>}
              </>
            )}
          </>
        )}

        {/* Tab 2: Set Stop-Loss */}
        {tab === 1 && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#4A3728' }}>Market</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#4A3728' }}>Entry</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#4A3728' }}>Current</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#4A3728', width: 140 }}>Stop-Loss</TableCell>
                    <TableCell sx={{ width: 80 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map(pos => (
                    <TableRow key={pos.id}>
                      <TableCell sx={{ fontSize: 13, maxWidth: 220 }}>
                        <Typography variant="body2" noWrap>{pos.market_title || pos.ticker}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13 }}>${pos.entry_price.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13 }}>${pos.current_price.toFixed(2)}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="e.g. 0.45"
                          value={stopLossValues[pos.id] ?? ''}
                          onChange={e => {
                            setStopLossValues(prev => ({ ...prev, [pos.id]: e.target.value }));
                            setSavedIds(prev => { const n = new Set(prev); n.delete(pos.id); return n; });
                          }}
                          error={!!saveErrors[pos.id]}
                          helperText={saveErrors[pos.id]}
                          disabled={savingId === pos.id || savingAll}
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
                          inputProps={{ style: { width: 80 } }}
                        />
                      </TableCell>
                      <TableCell>
                        {savedIds.has(pos.id) ? (
                          <CheckCircle sx={{ color: '#27AE60', fontSize: 20 }} />
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSaveStopLoss(pos.id)}
                            disabled={!stopLossValues[pos.id] || savingId === pos.id || savingAll}
                            sx={{ textTransform: 'none', fontSize: 12 }}
                          >
                            {savingId === pos.id ? <CircularProgress size={14} /> : 'Save'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Quantato checks stop-losses every 10 seconds and will auto-sell if triggered. Values are in dollars (e.g. 0.45 = 45 cents).
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {tab === 0 && !exitResults && (
          <>
            <Button onClick={handleClose} disabled={exitLoading} color="inherit">Cancel</Button>
            <Button
              variant="contained"
              onClick={handleConfirmExit}
              disabled={exitLoading}
              sx={{ backgroundColor: '#C0392B', fontWeight: 700, '&:hover': { backgroundColor: '#A93226' } }}
              startIcon={exitLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {exitLoading ? 'Exiting...' : `Confirm Exit (${positions.length})`}
            </Button>
          </>
        )}
        {tab === 1 && (
          <>
            <Button onClick={handleClose} disabled={savingAll} color="inherit">Close</Button>
            <Button
              variant="contained"
              onClick={handleSaveAll}
              disabled={!hasChanges || savingAll}
              sx={{ backgroundColor: '#3A8C8C', fontWeight: 700, '&:hover': { backgroundColor: '#2A6666' } }}
              startIcon={savingAll ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {savingAll ? 'Saving...' : 'Save All'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
